import { useState, useMemo, useCallback } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { useQuery, useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';
import { GenerativeModel } from 'entities/generative-models/types';
import { getGenerativeModels, deleteGenerativeModel } from 'entities/generative-models/api';
import { getErrorMessage } from 'shared/helpers/getErrorMessage';
import { ColorPalette } from 'shared/consts/colorPalette';
import { Button } from 'shared/ui/Button';
import { Loader } from 'shared/ui/Loader';
import { TableWithHeader } from 'shared/ui/tables/TableWithHeader';
import GenerativeInstructions from './partials/generative-instructions';
import GenerativeModal from './partials/generative-modal';
import {
	GenerativeModelColumnsType,
	GenerativeModelsTableColumns,
} from './partials/columns/GenerativeModelsTableColumns';

const GenerativeModelPage = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [openModal, setOpenModal] = useState(false);
	const [modelDetails, setModelDetails] = useState<GenerativeModel>();

	const {
		data: generativeModelsResponse,
		isLoading: getIsLoading,
		refetch,
	} = useQuery({
		queryKey: ['get/generative-models'],
		queryFn: () => getGenerativeModels(),
		onError: (err) => {
			const errorMessage = getErrorMessage(err);
			enqueueSnackbar({ message: errorMessage, variant: 'error' });
		},
	});

	const { mutate: deleteFile, isLoading: deleteIsLoading } = useMutation({
		mutationKey: ['delete/generative-model-file'],
		mutationFn: deleteGenerativeModel,
		onSuccess: () => refetch(),
		onError: (err) => {
			const errorMessage = getErrorMessage(err);
			enqueueSnackbar({ message: errorMessage, variant: 'error' });
		},
	});

	const editModelHandler = useCallback((model: GenerativeModel) => {
		setModelDetails(model);
		setOpenModal(true);
	}, []);

	const deleteModelHandler = useCallback((model: GenerativeModel) => {
		deleteFile({ generativeModelId: model._id });
	}, []);

	const settingsModelHandler = useCallback((model: GenerativeModel) => {
		navigate(`/generative-model/upload/${model._id}`);
	}, []);

	const preparedModels: GenerativeModelColumnsType[] = useMemo(() => {
		if (!generativeModelsResponse?.data.length) return [];
		return generativeModelsResponse?.data.map((item) => ({
			id: item._id,
			editHandler: editModelHandler,
			deleteHandler: deleteModelHandler,
			settingsHandler: settingsModelHandler,
			...item,
		}));
	}, [generativeModelsResponse]);

	if (deleteIsLoading || getIsLoading) return <Loader />;

	return (
		<>
			<GenerativeModal
				open={openModal}
				onClose={() => setOpenModal(false)}
				modelDetails={modelDetails}
			/>

			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
				<Typography variant={'h4'} color={ColorPalette.astronaut}>
					{t('sidebar.menu./generative-model')}
				</Typography>
				<Button onClick={() => setOpenModal(true)}>{t('custom_model.create_model')}</Button>
			</Box>
			<Divider color={ColorPalette.periwinkle} sx={{ margin: '44px 0' }} />

			{generativeModelsResponse?.data?.length ? (
				<TableWithHeader columns={GenerativeModelsTableColumns} rows={preparedModels} />
			) : (
				<GenerativeInstructions />
			)}
		</>
	);
};

export default GenerativeModelPage;
